/**
 * Rubato product page.
 *
 * 1. Description clamped to three lines with an inline Read more.
 * 2. The size row always matches the Add to cart width, so the buttons share it equally.
 * 3. Notify me on a sold out variant, but only when a Klaviyo public key is set.
 */

/* On its own line under the clamped text, so it no longer continues the ellipsis. */
const READ_MORE_LABEL = 'Read more';

function setUpReadMore(root = document) {
  for (const description of root.querySelectorAll('[data-rubato-description]')) {
    if (description.dataset.rubatoWired === 'true') continue;
    description.dataset.rubatoWired = 'true';

    const body = description.querySelector('.rubato-description__body');
    if (!body) continue;

    // Only offer Read more when the text actually overflows three lines
    if (body.scrollHeight <= body.clientHeight + 1) continue;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'rubato-description__more';
    button.textContent = READ_MORE_LABEL;
    button.setAttribute('aria-expanded', 'false');

    button.addEventListener('click', () => {
      const expanded = description.classList.toggle('rubato-description--expanded');
      button.setAttribute('aria-expanded', String(expanded));
      button.hidden = expanded;
    });

    description.append(button);
  }
}

/**
 * Back in stock.
 *
 * The link under the sizes opens the sheet, the sheet posts to Klaviyo's public client
 * endpoint with the variant id, and a success swaps the form for one line of thanks and closes
 * after two seconds. The public key comes from theme settings; with none set the form is
 * disabled in Liquid and this never runs. Round 37, item 1.
 */
const KLAVIYO_BIS = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

function notifySheet() {
  return document.querySelector('[data-rubato-notify-sheet]');
}

/**
 * Which app takes back in stock requests, from the theme setting rendered onto the buy block.
 * Amp for launch, Klaviyo after. Defaults to Amp when the attribute is missing, which matches
 * the setting's own default, so a stale cached block does not silently change the behaviour.
 */
function backInStockProvider() {
  const holder = document.querySelector('[data-rubato-bis]');
  return holder?.dataset.rubatoBis || 'amp';
}

/**
 * Hand Amp the sold-out variant and open its modal.
 *
 * BISPopover.variantChanged tells Amp which variant the request is for and toggle opens the
 * panel. Both were confirmed against the app on the development theme rather than taken from
 * documentation: Amp lists only the unavailable variants, so the id has to be the sold-out one
 * and not whatever the buy form last posted.
 */
/**
 * Round 61 part 24. Amp's modal wears our type and colours.
 *
 * Amp draws the modal inside an iframe it injects with no src. That is not a wall: a frameless
 * iframe inherits the parent's origin, so its document is ours to write to, and Amp's app
 * settings reach only the handful of colours in its popup_theme, not the typeface, the radii
 * or the button. The rules live in snippets/rubato-amp-modal-css.liquid, in a template in the
 * page, and are copied in here.
 *
 * Two of Amp's habits are worked around. There are two #BIS_frame iframes and the first is
 * the hidden one, so the visible one is chosen by measurement rather than by order. And Amp
 * rebuilds the document inside the frame when it reopens, which takes the stylesheet out
 * again, so the copy is attempted for a short while after each open and stops as soon as the
 * rules are in and the document is the one they were put into.
 */
const AMP_STYLE_ID = 'rubato-amp-modal-css';
const AMP_DEFAULT_SUBMIT = 'Notify me when available';

/* Every frame that holds a modal, not the one that looks biggest. Measuring the frame was the
   first attempt and it dressed the desktop and missed the phone: at 390 the frame Amp shows is
   not sized when the first attempts run. What identifies the right document is that it has a
   #BISModal in it, so that is what is asked. */
function ampDocuments() {
  const docs = [];
  for (const frame of document.querySelectorAll('iframe#BIS_frame')) {
    /* Reading contentDocument throws if the frame ever turns out to be cross-origin, which it
       is not today and need not stay true for the button to keep working. */
    let doc;
    try { doc = frame.contentDocument; } catch (error) { continue; }
    if (doc && doc.head && doc.getElementById('BISModal')) docs.push(doc);
  }
  return docs;
}

function dressAmp() {
  const template = document.querySelector('[data-rubato-amp-css]');
  if (!template) return false;

  const docs = ampDocuments();
  if (!docs.length) return false;

  for (const doc of docs) {
    if (doc.getElementById(AMP_STYLE_ID)) continue;
    const style = doc.createElement('style');
    style.id = AMP_STYLE_ID;
    style.textContent = template.innerHTML;
    doc.head.appendChild(style);
  }
  return true;
}

/**
 * Round 61 part 25. show({ variantId }), not variantChanged(id) and toggle().
 *
 * variantChanged takes no arguments. Whatever is handed to it is discarded, and it works out
 * the variant for itself: the ?variant= parameter, failing that the value of a
 * select.single-option-selector, failing that product.variants[0]. On a Horizon size row
 * there is no single-option-selector, so it landed on the first variant every time. With the
 * Geelongora that was XS and XS was also the only sold-out size, so the right answer came out
 * of the wrong reasoning and the fault stayed hidden until a product with two sold-out sizes.
 * On Fireman's Jacket in Deep, choosing L opened the modal on XS.
 *
 * The method that actually sets the field is the form's show, which takes an options object
 * and calls selectVariant with options.variantId. Popover.show passes its argument straight
 * through, so that is what is called. toggle() is not used at all: it calls show with nothing,
 * which is the whole of the bug, and there is nothing to toggle when the intent is always to
 * open.
 */
/* The select Amp puts the size in is #variants, and selectVariant sets its selectedIndex
   without firing a change event, so setting the index is the whole of it here too. Silent
   when the option is not there: a size Amp does not list is not a size to hold. */
function holdVariant(variant) {
  for (const doc of ampDocuments()) {
    const select = doc.getElementById('variants');
    if (!select) continue;
    if (String(select.value) === String(variant)) continue;
    const index = [...select.options].findIndex((option) => String(option.value) === String(variant));
    if (index > -1) select.selectedIndex = index;
  }
}

/**
 * Round 61 part 25. Two things the stylesheet cannot do on its own.
 *
 * A dropdown holding one option is not a choice, it is a label wearing a control's clothes.
 * Where only one size of a product is gone, the select is hidden and the size is written out
 * as text. Where more than one is gone the select stays, because switching between them
 * without closing the drawer is worth having.
 *
 * And Escape. The drawer is a document of its own, so a key pressed with the focus inside it
 * never reaches the page, and a key pressed outside it never reaches Amp. The listener goes
 * on both, and both call Amp's own hide so the state it keeps stays true.
 */
function closeAmp() {
  try { window.BISPopover?.hide?.(); } catch (error) { /* already gone */ }
  /* The next open starts hidden again, so a rebuilt document cannot be seen undressed. */
  for (const frame of document.querySelectorAll('iframe#BIS_frame')) {
    delete frame.dataset.rubatoDressed;
  }
}

/* Shown once the document inside is carrying our stylesheet, and unconditionally after a
   moment whether it is or not: Amp's own modal is a worse outcome than a flicker, and an
   invisible one is worse than either. */
function revealAmp(force = false) {
  for (const frame of document.querySelectorAll('iframe#BIS_frame')) {
    if (frame.dataset.rubatoDressed === 'true') continue;
    if (force) { frame.dataset.rubatoDressed = 'true'; continue; }
    let doc;
    try { doc = frame.contentDocument; } catch (error) { continue; }
    if (doc?.getElementById('BISModal') && doc.getElementById(AMP_STYLE_ID)) {
      frame.dataset.rubatoDressed = 'true';
    }
  }
}

/**
 * Round 61 part 26. Amp is pointed at the new product after a colour swap.
 *
 * Amp reads its product once, at init, from location.pathname + '.js', and keeps it in
 * this.product with the variants indexed beside it. The colour swap replaces the section and
 * rewrites the URL without a load, so Amp went on offering the colour the customer had left:
 * choosing Deep and opening the flyout gave Olive Drab.
 *
 * The product is cleared and getProducts() is called again, which re-reads the path that is
 * now the new colour's, and createUI() rebuilds the form against it. The old frame is taken
 * out first, because createUI makes a new one and two frames answering to the same id is how
 * every measurement in this area has gone wrong so far.
 *
 * Kept as a promise so a click arriving mid-rebuild waits for it rather than opening the
 * flyout on whichever product Amp happens to be holding.
 */
let ampReset = null;

function resetAmpProduct() {
  const bis = window.BISPopover;
  if (!bis) return Promise.resolve();

  ampReset = (async () => {
    try {
      try { bis.hide?.(); } catch (error) { /* nothing open */ }
      for (const frame of document.querySelectorAll('iframe#BIS_frame')) frame.remove();

      bis.product = null;
      bis.productHandle = null;
      bis.variants = [];
      bis._variantsById = {};
      bis._variantsByTitle = {};
      bis.form = null;
      bis.button = null;

      /* BIS.Promise is not a Promise: then() takes a callback and gives back the same object,
         so it is wrapped rather than awaited. The timeout is the network failing quietly. */
      await new Promise((resolve) => {
        let settled = false;
        const done = () => { if (!settled) { settled = true; resolve(); } };
        setTimeout(done, 4000);
        try { bis.getProducts().then(done); } catch (error) { done(); }
      });

      bis.createUI();
    } catch (error) {
      /* Amp's own console will carry the detail. A stale flyout is better than no button. */
    }
  })();

  return ampReset;
}

document.addEventListener('rubato:product-swapped', () => { resetAmpProduct(); });

/**
 * Round 61 part 27. Amp's dropdown becomes the page's size row.
 *
 * A select is not how this site asks for a size, and the row it is replaced with is built from
 * the page rather than from Amp: the same sizes in the same order, because the order comes from
 * reading the page's own row top to bottom. Only the sold-out ones appear, since a size that is
 * in stock is not a size to be told about.
 *
 * The select stays, hidden, and remains the record: a tap on the row sets its index, so what
 * Amp submits is what the row shows. Built once per document and only kept in step afterwards,
 * because rebuilding it on the 100ms tick would take the buttons out from under a finger.
 */
let ampUserPicked = false;

function soldOutSizes() {
  return [...document.querySelectorAll('.product-details .variant-option--buttons input[type="radio"]')]
    .filter((input) => input.dataset.optionAvailable === 'false')
    .map((input) => ({ label: (input.value || '').trim(), variant: input.dataset.variantId || '' }))
    .filter((size) => size.label && size.variant);
}

function buildAmpSizeRow(doc, select) {
  const sizes = soldOutSizes();
  /* Amp is the authority on what it will accept. A size the page calls sold out that Amp does
     not list is left out rather than offered and refused. */
  const offered = sizes.filter((size) => [...select.options].some((option) => String(option.value) === String(size.variant)));
  if (offered.length < 2) return null;

  let row = doc.querySelector('.rubato-amp-sizes');
  const signature = offered.map((size) => size.variant).join(',');
  if (row && row.dataset.rubatoSizes === signature) return row;

  row?.remove();
  row = doc.createElement('div');
  row.className = 'rubato-amp-sizes';
  row.dataset.rubatoSizes = signature;
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', 'Size');

  for (const size of offered) {
    const option = doc.createElement('button');
    option.type = 'button';
    option.className = 'rubato-amp-size-option';
    option.dataset.variantId = size.variant;
    option.setAttribute('aria-pressed', 'false');
    option.textContent = size.label;
    row.append(option);
  }

  row.addEventListener('click', (event) => {
    /* Not `instanceof Element`. The clicked node belongs to the frame's realm and Element here
       is ours, so the test is false for every real click and the row did nothing. Asking the
       node whether it can do closest is the check that works across the two documents. */
    const option = typeof event.target?.closest === 'function' ? event.target.closest('.rubato-amp-size-option') : null;
    if (!option) return;
    const index = [...select.options].findIndex((o) => String(o.value) === String(option.dataset.variantId));
    if (index < 0) return;
    select.selectedIndex = index;
    /* Amp reads the select on submit and may also listen for the change; both are covered. */
    select.dispatchEvent(new Event('change', { bubbles: true }));
    /* From here the row is the customer's, not the page's: holdVariant stops overruling it. */
    ampUserPicked = true;
    markAmpSizeRow(row, select);
  });

  select.parentNode?.insertBefore(row, select);
  return row;
}

function markAmpSizeRow(row, select) {
  for (const option of row.querySelectorAll('.rubato-amp-size-option')) {
    const chosen = String(option.dataset.variantId) === String(select.value);
    const now = chosen ? 'true' : 'false';
    if (option.getAttribute('aria-pressed') !== now) option.setAttribute('aria-pressed', now);
  }
}

function shapeAmp() {
  for (const doc of ampDocuments()) {
    const select = doc.getElementById('variants');

    if (select) {
      const single = select.options.length === 1;
      select.classList.toggle('rubato-one-size', single);
      let line = doc.querySelector('.rubato-amp-size');
      if (single) {
        if (!line) {
          line = doc.createElement('p');
          line.className = 'rubato-amp-size';
          select.parentNode?.insertBefore(line, select);
        }
        const label = (select.options[0].text || '').trim();
        if (line.textContent !== label) line.textContent = label;
      } else if (line) {
        line.remove();
      }

      const row = single ? null : buildAmpSizeRow(doc, select);
      if (row) markAmpSizeRow(row, select);
      /* Hidden whenever something of ours stands in for it, and only then. */
      select.classList.toggle('rubato-amp-hidden', !!row);
      if (!row && !single) doc.querySelector('.rubato-amp-sizes')?.remove();
    }

    /* Amp ships wrappers it does not always fill: a contact container that is 68px of nothing
       on an email-only form, and a message holder that is empty until something goes wrong.
       Empty or not they were flex children, so each one took a 24 gap and stood the footnote
       88 below the button instead of 16. Collapsed while they hold nothing, and back the
       moment they hold something. */
    for (const wrapper of doc.querySelectorAll('#customer-contact-container, #message_holder, .form-group')) {
      const filled = !!wrapper.textContent.trim() || !!wrapper.querySelector('input, select, button, textarea, a');
      wrapper.classList.toggle('rubato-amp-empty', !filled);
    }

    /* Round 62. The address field lets go when the form is sent.

       It kept focus through the submit, so Amp's confirmation appeared with the emptied field
       still ringed above it, the same fault the footer had. Bound once per document, on the
       submit rather than on a timer, so the blur happens at the moment the customer commits
       and not a tick later. */
    for (const submit of doc.querySelectorAll('#BISModal button[type="submit"], #BISModal form button:not(.rubato-amp-size-option)')) {
      if (submit.dataset.rubatoBlur === 'true') continue;
      submit.dataset.rubatoBlur = 'true';
      submit.addEventListener('click', () => {
        for (const field of doc.querySelectorAll('#BISModal input')) field.blur();
      });
    }
    const ampForm = doc.querySelector('#BISModal form');
    if (ampForm && ampForm.dataset.rubatoBlur !== 'true') {
      ampForm.dataset.rubatoBlur = 'true';
      ampForm.addEventListener('submit', () => {
        for (const field of doc.querySelectorAll('#BISModal input')) field.blur();
      });
    }

    /* Once Amp says it has the address there is nothing left to submit, so the button goes.
       Keyed on the panel Amp shows rather than on a flag of ours, because Amp owns when that
       moment is. */
    const done = doc.querySelector('#BISModal .completed_message, #BISModal .alert-success');
    const registered = !!(done && getComputedStyle(done).display !== 'none' && done.textContent.trim());
    for (const submit of doc.querySelectorAll('#BISModal button[type="submit"], #BISModal .form-group')) {
      submit.classList.toggle('rubato-amp-registered', registered);
    }

    /* The submit's wording. Amp's own caption setting is button_caption, and that is the
       floating trigger we hide; the string in the modal is hardcoded in Amp's widget and no
       setting reaches it. Replaced here so the flyout says what the button that opened it
       says, and only while it still reads Amp's default: once Amp puts its own state on the
       button, that state is Amp's to show. */
    for (const submit of doc.querySelectorAll('#BISModal button:not(.close):not(.action-close):not(.rubato-amp-size-option)')) {
      if (submit.textContent.trim() === AMP_DEFAULT_SUBMIT) submit.textContent = NOTIFY_LABEL;
    }

    /* Once per document. A rebuild makes a new one and it is listened to again. */
    if (doc.documentElement.dataset.rubatoEscape === 'true') continue;
    doc.documentElement.dataset.rubatoEscape = 'true';
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAmp();
    });
  }
}

/* The page's own Escape, for when the focus never entered the frame. Bound once, and it does
   nothing unless a frame is actually holding a modal. */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  if (!ampDocuments().length) return;
  closeAmp();
});

async function openAmp(button) {
  /* A colour swap may still be rebuilding Amp. Waiting is the difference between the flyout
     naming the colour on screen and the colour the customer left. */
  if (ampReset) { try { await ampReset; } catch (error) { /* rebuilt or not, carry on */ } }

  const bis = window.BISPopover;
  if (!bis) return false;
  const variant = Number(button.dataset.variantId || 0);
  try {
    if (typeof bis.show === 'function') {
      /* Each open starts from the page's size again. */
      ampUserPicked = false;
      bis.show(variant ? { variantId: variant } : {});
      /* The safety net for the reveal, whatever happens to the dressing. */
      setTimeout(() => revealAmp(true), 1200);

      /* Every 100ms for six seconds, and it does not stop on the first success. Amp rebuilds
         the document inside the frame after the modal is already up; the rebuild takes the
         stylesheet out and puts the select back to its first option, so both are reasserted
         for the whole window rather than done once. Six seconds is well past the rebuild and
         costs two queries on an empty list afterwards. */
      /* Straight away as well as on the timer: the sooner the stylesheet lands, the shorter
         the frame stays hidden. */
      dressAmp();
      shapeAmp();
      if (variant) holdVariant(variant);
      revealAmp();

      let tries = 0;
      const timer = setInterval(() => {
        dressAmp();
        shapeAmp();
        /* Only until the customer touches the row. After that the row is the answer and
           reasserting the page's size every 100ms would undo every tap. */
        if (variant && !ampUserPicked) holdVariant(variant);
        revealAmp();
        if (++tries > 60) clearInterval(timer);
      }, 100);
      return true;
    }
  } catch (error) {
    /* Amp's own console will carry the detail; the button must not die with it. */
  }
  return false;
}

async function subscribeBackInStock(sheet, email, variantId) {
  const key = sheet.dataset.klaviyoKey;
  if (!key || !variantId) throw new Error('not configured');
  const body = {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: ['EMAIL'],
        profile: { data: { type: 'profile', attributes: { email } } },
      },
      relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } },
    },
  };
  const response = await fetch(`${KLAVIYO_BIS}?company_id=${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
  return true;
}

/**
 * Opening the sheet from whichever buy button is in Notify me mode.
 *
 * Registered once, in the capture phase on the document, so it runs before Horizon's own
 * click handler on the Add to cart button and stops the event reaching it. The button is a
 * type="button" while it is in this mode, so nothing can submit the form either way.
 */
function openNotifySheet() {
  const sheet = notifySheet();
  if (!(sheet instanceof HTMLDialogElement)) return;

  /* The variant the shopper is looking at, not the one the page opened on. */
  const checked = document.querySelector('.product-details .variant-option input[type="radio"]:checked');
  const field = document.querySelector('.product-details form [name="id"]');
  const size = sheet.querySelector('[data-rubato-notify-size]');
  if (size) size.textContent = checked?.value ?? '';
  sheet.dataset.variantId = field?.value ?? '';
  sheet.showModal();
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-rubato-notify-mode="true"]');
    if (button && button.dataset.backInStock === 'amp') {
      event.preventDefault();
      /* Stopped as well as prevented. The sticky bar forwards its own button's clicks to the
         main one, and both are in notify mode, so without this a tap on the bar opened the
         flyout twice. */
      event.stopPropagation();
      openAmp(button);
      return;
    }
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    openNotifySheet();
  },
  true
);

function setUpNotify() {
  const sheet = notifySheet();
  if (!(sheet instanceof HTMLDialogElement) || sheet.dataset.rubatoWired === 'true') return;
  sheet.dataset.rubatoWired = 'true';

  sheet.querySelector('[data-rubato-notify-close]')?.addEventListener('click', () => sheet.close());
  sheet.addEventListener('click', (event) => {
    if (event.target === sheet) sheet.close();
  });

  const form = sheet.querySelector('[data-rubato-notify-form]');
  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const input = sheet.querySelector('.rubato-notify-sheet__input');
    const submit = sheet.querySelector('[data-rubato-notify-submit]');
    if (!(input instanceof HTMLInputElement) || !input.value || !input.checkValidity()) {
      input?.focus();
      return;
    }
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    try {
      await subscribeBackInStock(sheet, input.value, sheet.dataset.variantId);
      form.hidden = true;
      sheet.querySelector('[data-rubato-notify-copy]')?.setAttribute('hidden', '');
      sheet.querySelector('[data-rubato-notify-thanks]')?.removeAttribute('hidden');
      setTimeout(() => {
        sheet.close();
        form.hidden = false;
        sheet.querySelector('[data-rubato-notify-copy]')?.removeAttribute('hidden');
        sheet.querySelector('[data-rubato-notify-thanks]')?.setAttribute('hidden', '');
        input.value = '';
        if (submit instanceof HTMLButtonElement) submit.disabled = false;
      }, 2000);
    } catch {
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
      input.setCustomValidity('That did not go through. Try again.');
      input.reportValidity();
      input.setCustomValidity('');
    }
  });
}

/**
 * Klaviyo back in stock. Klaviyo renders its own button inside the element carrying
 * klaviyo-bis-trigger, so the theme only marks the spot, hides its stand-in once
 * Klaviyo is on the page, and re-initialises Klaviyo when the variant changes.
 */
function setUpBackInStock(root = document) {
  const triggers = root.querySelectorAll('[klaviyo-bis-trigger]');
  if (!triggers.length) return;

  const klaviyo = window._klaviyo ?? window.klaviyo;
  const present = Boolean(klaviyo);

  for (const trigger of triggers) {
    trigger.classList.toggle('rubato-bis--klaviyo', present);
  }

  if (present && typeof klaviyo.init === 'function') {
    try {
      klaviyo.init();
    } catch {
      // Klaviyo not ready yet; the next variant change tries again
    }
  }
}

/**
 * The sticky bar shows only while the main Add to cart button is out of view, and its
 * button clicks the real one so the cart drawer opens the usual way.
 */
const MAIN_BUTTON = '.product-details .add-to-cart-button';
const SIZE_ROW = '.product-details .variant-option--buttons';

let stickyObserver = null;
let stickyTarget = null;
let stickyRow = null;

/**
 * Shows or hides the bar from where the main button and the size row currently sit.
 *
 * The button's rule is the old one: the bar comes up once the button has gone off the top,
 * never while it is still below. The size row carries the same rule, so the bar cannot come
 * up over the row and put its own Add to cart where the sizes are. Round 32, item 2.
 */
function applyStickyState() {
  const bar = document.querySelector('[data-rubato-sticky-bar]');
  const target = document.querySelector(MAIN_BUTTON);
  if (!bar || !target) return;
  const row = document.querySelector(SIZE_ROW);
  const scrolledPast = target.getBoundingClientRect().bottom <= 0;
  const rowOut = (() => {
    if (!row) return true;
    const box = row.getBoundingClientRect();
    return box.bottom <= 0 || box.top >= (window.innerHeight || document.documentElement.clientHeight);
  })();
  const show = scrolledPast && rowOut;
  bar.hidden = !show;
  document.body.classList.toggle('rubato-sticky-bar-visible', show);
}

/**
 * The variant picker re-renders the buy column, which can replace the button the
 * observer watches. So the target is re-read after every update, and the bar's
 * visibility is applied at once rather than waiting for the next scroll.
 */
/**
 * The bar and its size sheet are moved to the body on first run. A variant change
 * re-renders the buy column, and anything left inside it can be replaced mid update;
 * out here nothing the section renders can touch them.
 */
function hoistStickyBar() {
  for (const selector of ['[data-rubato-sticky-bar]', '[data-rubato-size-sheet]']) {
    const nodes = [...document.querySelectorAll(selector)];
    if (!nodes.length) continue;
    const kept = nodes.find((node) => node.parentElement === document.body) ?? nodes[0];
    if (kept.parentElement !== document.body) document.body.append(kept);
    // A re-render can hand us a second copy from inside the section; it is dropped
    for (const node of nodes) if (node !== kept) node.remove();
  }
}

function setUpStickyBar() {
  hoistStickyBar();

  const bar = document.querySelector('[data-rubato-sticky-bar]');
  const target = document.querySelector(MAIN_BUTTON);
  if (!bar || !target) return;

  if (bar.dataset.rubatoWired !== 'true') {
    bar.dataset.rubatoWired = 'true';
    bar.querySelector('[data-rubato-sticky-add]')?.addEventListener('click', () => {
      const button = document.querySelector(MAIN_BUTTON);
      if (button instanceof HTMLElement) button.click();
    });
  }

  const row = document.querySelector(SIZE_ROW);
  if (target !== stickyTarget || row !== stickyRow || !stickyObserver) {
    stickyObserver?.disconnect();
    stickyTarget = target;
    stickyRow = row;
    stickyObserver = new IntersectionObserver(applyStickyState, { threshold: 0 });
    stickyObserver.observe(target);
    if (row) stickyObserver.observe(row);
  }

  applyStickyState();
}

/**
 * A sticky buy column taller than the viewport scrolls with the page and pins its
 * bottom to the viewport bottom; one that fits pins 96px under the header.
 */
function positionBuyColumn() {
  const column = document.querySelector('.product-details');
  if (!(column instanceof HTMLElement)) return;
  if (window.matchMedia('(max-width: 749px)').matches) {
    column.style.top = '';
    return;
  }

  const header = document.querySelector('#header-component');
  const headerHeight = header instanceof HTMLElement ? header.offsetHeight : 64;
  const restingTop = headerHeight + 96;

  /* The column pins 96px under the header, not against it, so the room it has is measured
     from there. Measuring from the header alone made the test 96px too generous: at 1280x720
     the column stands 561.8 against 560 of room, the test read it as fitting, and the column
     scrolled off the top instead of pinning. Round 39. */
  const available = window.innerHeight - restingTop;
  const height = column.scrollHeight;

  column.style.top = height > available
    ? `${Math.round(window.innerHeight - height)}px`
    : `${restingTop}px`;
}

/**
 * Keeping the header off the buy column.
 *
 * When the column is taller than the window it pins its bottom to the window's bottom, which
 * on a short window puts its top above the header's line: at 1440x600 it rests at 17 and the
 * header's foot is at 64, so the header covered 46px of the title and 9 of the price the
 * moment it came back on a scroll up. The column drops by exactly what it takes to clear the
 * header whenever the header is on screen, and returns when the header leaves, moving on the
 * header's own transition so the two read as one movement. Round 42, item 1.
 */
const BUY_SHIFT = '--rubato-buy-shift';

function applyHeaderClearance() {
  const column = document.querySelector('.product-details');
  if (!(column instanceof HTMLElement)) return;

  if (window.matchMedia('(max-width: 749px)').matches) {
    column.style.setProperty(BUY_SHIFT, '0px');
    return;
  }

  const header = document.querySelector('#header-component');
  const headerBottom = header instanceof HTMLElement ? header.getBoundingClientRect().bottom : 0;

  /* Where the column would sit with no shift on it. Taking the shift out of the box rather
     than reading the style's top covers both states in one sum: at the top of the page the
     column is still in flow, below the header already, and needs nothing; once it is pinned
     against the window's bottom it sits above the header's line and needs the difference.
     The translate is read off the rendered matrix, not off the property we set, so a
     measurement taken mid transition is still the truth. */
  let applied = 0;
  const matrix = getComputedStyle(column).transform;
  if (matrix && matrix !== 'none') {
    try {
      applied = new DOMMatrixReadOnly(matrix).m42;
    } catch {
      applied = 0;
    }
  }
  const rect = column.getBoundingClientRect();
  const restingTop = rect.top - applied;
  const restingBottom = rect.bottom - applied;

  /* A header that has scrolled away is not in the way. */
  const wanted = headerBottom > 0 ? Math.max(0, Math.round(headerBottom - restingTop)) : 0;

  /* The column belongs to the product section and may not leave it. Sticky positioning keeps
     it inside on its own, but a translate does not: it moves what is painted wherever the
     number says. Once the gallery's foot has come up to meet the column's there is no room
     left to shift into, and without this the column rode out of the section on the way back up
     and painted over You may also like, by 280px at 1440x900 and 521 at 1440x600. Round 45. */
  const section = column.closest('product-component') ?? column.parentElement;
  const room = section
    ? Math.max(0, Math.round(section.getBoundingClientRect().bottom - restingBottom))
    : wanted;

  const clearance = Math.min(wanted, room);
  column.style.setProperty(BUY_SHIFT, `${clearance}px`);
}

let clearancePending = false;
function queueHeaderClearance() {
  if (clearancePending) return;
  clearancePending = true;
  requestAnimationFrame(() => {
    clearancePending = false;
    applyHeaderClearance();
  });
}

function watchHeaderClearance() {
  for (const target of new Set([window, document.querySelector('.page-wrapper')])) {
    if (!target) continue;
    target.removeEventListener('scroll', queueHeaderClearance);
    target.addEventListener('scroll', queueHeaderClearance, { passive: true });
  }
  window.addEventListener('scrollend', queueHeaderClearance, { passive: true });
  window.addEventListener('resize', queueHeaderClearance);
}

const repositionColumn = () =>
  requestAnimationFrame(() => {
    positionBuyColumn();
    applyHeaderClearance();
  });
window.addEventListener('resize', repositionColumn);
document.addEventListener('toggle', repositionColumn, true);

/**
 * The size sheet holds a copy of the buy column's own variant picker, so the buttons
 * are the same component in the same states. A tap drives the real input.
 */
function setUpSizeSheet() {
  const sheet = document.querySelector('[data-rubato-size-sheet]');
  const open = document.querySelector('[data-rubato-size-open]');
  const body = sheet?.querySelector('[data-rubato-size-body]');
  if (!sheet || !open || !body || sheet.dataset.rubatoWired === 'true') return;
  sheet.dataset.rubatoWired = 'true';

  const fill = () => {
    body.replaceChildren();
    for (const fieldset of document.querySelectorAll('.product-details .variant-option--buttons')) {
      const copy = fieldset.cloneNode(true);
      copy.removeAttribute('ref');
      copy.removeAttribute('data-option-id');
      for (const input of copy.querySelectorAll('input[type="radio"]')) {
        // A copy must not share a radio group with the picker it was taken from
        input.name = `${input.name}-rubato-sheet`;
        input.removeAttribute('id');
        input.tabIndex = -1;
      }
      body.append(copy);
    }
  };

  const pick = (label) => {
    const input = label.querySelector('input[type="radio"]');
    if (!input) return;
    /* Round 61 part 27. A sold-out size is choosable here because it is choosable in the row
       this sheet is a copy of. Round 55 made the page's row take them, so that the buy button
       could become Notify me; the sheet kept refusing them, which left the sticky bar the one
       place on the product where a sold-out size could not be asked about. The two rows drive
       the same inputs, so letting the tap through is the whole of it: the real input's click
       carries the state to the button, the bar and Amp alike. */

    const real = [...document.querySelectorAll('.product-details .variant-option--buttons input[type="radio"]')].find(
      (el) => el.value === input.value && el.dataset.optionName === input.dataset.optionName
    );

    sheet.close();
    if (real instanceof HTMLElement) real.click();
    requestAnimationFrame(refreshStickyBar);
  };

  open.addEventListener('click', () => {
    fill();
    sheet.showModal();
  });
  sheet.querySelector('[data-rubato-size-close]')?.addEventListener('click', () => sheet.close());
  sheet.addEventListener('click', (event) => {
    if (event.target === sheet) sheet.close();
  });

  body.addEventListener('click', (event) => {
    const label = event.target instanceof Element ? event.target.closest('.variant-option__button-label') : null;
    if (!label) return;
    event.preventDefault();
    pick(label);
  });
}

/** Keeps the bar's price and size in step with the selected variant. */
function refreshStickyBar() {
  const bar = document.querySelector('[data-rubato-sticky-bar]');
  if (!bar) return;

  const price = document.querySelector('.product-details product-price .price');
  const size = document.querySelector('.product-details .variant-option:not(.variant-option--swatches) input:checked');

  const priceOut = bar.querySelector('[data-rubato-sticky-price]');
  const sizeOut = bar.querySelector('[data-rubato-sticky-size]');
  if (price && priceOut) priceOut.textContent = price.textContent?.trim() ?? '';
  // With nothing selected the bar must not keep claiming the last size
  if (sizeOut) sizeOut.textContent = size ? size.value ?? '' : '';
}

/**
 * Zoom view: the image opens fitted to the viewport, a click zooms to 2.5x centred on
 * the point clicked, a second click resets.
 */
function setUpZoom(root = document) {
  for (const dialog of root.querySelectorAll('.dialog-zoomed-gallery__dialog')) {
    if (dialog.dataset.rubatoWired === 'true') continue;
    dialog.dataset.rubatoWired = 'true';

    dialog.addEventListener('click', (event) => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement)) return;

      const zoomed = image.dataset.rubatoZoomed === 'true';
      if (zoomed) {
        image.style.transform = '';
        image.style.transformOrigin = '';
        image.dataset.rubatoZoomed = 'false';
        image.parentElement?.removeAttribute('data-rubato-zoomed');
        return;
      }

      const rect = image.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      image.style.transformOrigin = `${x}% ${y}%`;
      image.style.transform = 'scale(2.5)';
      image.dataset.rubatoZoomed = 'true';
      image.parentElement?.setAttribute('data-rubato-zoomed', 'true');
    });
  }
}

/**
 * Duplicate size guard. Adding a second size of a product already in the cart opens a
 * sheet first; the same size again is not intercepted.
 *
 * Round 61 part 26. It was firing when it had no business firing, twice over.
 *
 * It matched every click on the buy button, and on a sold-out size that button is Notify me.
 * With another size of the product in the cart, asking for a back in stock alert opened Need
 * help over the Amp flyout. The guard is about adding a second size to a basket; it has
 * nothing to say about a size that cannot be added at all.
 *
 * And it read a cached cart. The cache was refreshed on cart events, on a drawer mutation and
 * on visibilitychange, none of which is the same as knowing: emptying the cart and coming back
 * to the product left Need help firing on a cart that had nothing in it. The cart is read from
 * /cart.js at the moment of the click now, with no cache anywhere.
 *
 * Reading at click time means the decision is asynchronous, so every add is stopped first and
 * released a fetch later. That is the cost of the accuracy and it is one request against
 * Shopify's own cart endpoint. A failed read releases the add: a sale is never blocked on not
 * knowing.
 */
async function readCart() {
  try {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

function setUpSizeGuard() {
  const dialog = document.querySelector('[data-rubato-guard]');
  if (!dialog || dialog.dataset.rubatoWired === 'true') return;
  dialog.dataset.rubatoWired = 'true';

  let allowNext = false;

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element
        ? event.target.closest('.product-details .add-to-cart-button')
        : null;
      if (!button) return;

      /* Never on the Notify me path. The button is the same element in both states, and the
         state is on the element, so this is the whole of the test. */
      if (button.dataset.rubatoNotifyMode === 'true') return;
      if (button.dataset.backInStock === 'amp') return;
      if (button.disabled) return;

      if (allowNext) {
        allowNext = false;
        return;
      }

      const variantId = button.closest('form')?.querySelector('input[name="id"]')?.value;
      const productId = dialog.dataset.productId;
      if (!variantId || !productId) return; // never block a sale on missing data

      /* Stopped first, released after the read. preventDefault cannot be reached from inside
         a promise, so there is no version of this that reads the cart first. */
      event.preventDefault();
      event.stopImmediatePropagation();

      const release = () => {
        allowNext = true;
        button.click();
      };

      readCart().then((cart) => {
        if (!cart) { release(); return; } // a failed read is not a reason to block a sale

        const lines = cart.items ?? [];
        // The same size again is never intercepted, only a different size of the same product
        const sameSizeAlreadyInCart = lines.some((line) => String(line.variant_id) === String(variantId));
        const otherSizeInCart = lines.some(
          (line) => String(line.product_id) === String(productId) && String(line.variant_id) !== String(variantId)
        );

        if (!otherSizeInCart || sameSizeAlreadyInCart) { release(); return; }

        const email = dialog.dataset.supportEmail;
        const title = dialog.dataset.productTitle ?? '';
        const mailto = dialog.querySelector('[data-rubato-guard-email]');
        if (mailto) {
          mailto.href = email ? `mailto:${email}?subject=${encodeURIComponent('Size help: ' + title)}` : '#';
          mailto.hidden = !email;
        }

        if (!dialog.open) dialog.showModal();
      });
    },
    true
  );

  dialog.querySelector('[data-rubato-guard-continue]')?.addEventListener('click', () => {
    allowNext = true;
    dialog.close();
    document.querySelector('.product-details .add-to-cart-button')?.click();
  });

  // The same size guide the buy column opens, from inside the prompt
  dialog.querySelector('[data-rubato-guard-guide]')?.addEventListener('click', () => {
    dialog.close();
    const trigger = document.querySelector('[data-rubato-size-guide-open]');
    if (trigger instanceof HTMLElement) trigger.click();
  });

  dialog.querySelector('[data-rubato-guard-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
}

/**
 * The size row is a grid, not a stretch flex: at most six columns on mobile and eight on
 * desktop, so a seventh size wraps to a second row at the same width.
 */
/**
 * Switching colour without a page load.
 *
 * The sibling's product section is fetched through the Section Rendering API and
 * swapped in place, then the URL, title and canonical link are brought up to date.
 * A modified click is left alone, and a failed fetch falls back to navigation.
 *
 * The URL is replaced, never pushed. Back from a product goes to the page the customer came
 * from in one step, however many colours they tried.
 *
 * This has been both ways. Round 57, B-5 changed replaceState to pushState in b8a564d, so
 * that Back after stepping Dune, Cloud, Olive returned to Cloud rather than to the
 * collection; an audit had logged the old behaviour as "back after a colour change does not
 * return to the previous product". Round 61 part 15 changed it back, because the cost was
 * worse than the complaint: three colours meant three presses of Back to leave the product,
 * and a customer who has been browsing a colour row has no idea how many that is.
 *
 * Trying a colour is looking at one product, not visiting three. The trade is deliberate and
 * it is the round 57 behaviour that is given up: Back no longer returns to the previous
 * colour, it leaves the product.
 */
let swapController = null;

/**
 * What the product section is currently showing. Set on load and after every swap, so
 * popstate can tell whether the entry it landed on needs a swap or is already painted.
 */
let renderedProductUrl = window.location.pathname + window.location.search;

function productSection() {
  const details = document.querySelector('.product-details');
  return details ? details.closest('.shopify-section') : null;
}

function currentSizeValue() {
  const checked = document.querySelector('.product-details .variant-option--buttons input:checked');
  return checked ? checked.value : null;
}

/**
 * The size carries over when the new colour has it in stock. When it does not, the
 * picker is left with nothing selected and both buy buttons read Select size until a
 * size is chosen, so a size the customer never picked cannot reach the cart.
 */
const SELECT_SIZE_LABEL = 'Select size';

function labelNode(button) {
  if (!button) return null;
  return [...button.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
}

function awaitSizeChoice(inputs) {
  const buttons = [
    document.querySelector('.product-details .add-to-cart-button'),
    document.querySelector('[data-rubato-sticky-add]'),
  ].filter((button) => button instanceof HTMLButtonElement);

  const originals = [];
  for (const button of buttons) {
    const node = labelNode(button);
    originals.push({ button, node, text: node ? node.textContent : null });
    if (node) node.textContent = ` ${SELECT_SIZE_LABEL} `;
    button.disabled = true;
    button.classList.add('rubato-awaiting-size');
  }

  const release = () => {
    for (const { button, node, text } of originals) {
      if (node && text !== null && node.isConnected) node.textContent = text;
      button.disabled = false;
      button.classList.remove('rubato-awaiting-size');
    }
    // A variant change re-renders the buy buttons, so the fresh ones are cleared too
    for (const selector of ['.product-details .add-to-cart-button', '[data-rubato-sticky-add]']) {
      const button = document.querySelector(selector);
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.classList.remove('rubato-awaiting-size');
        const node = labelNode(button);
        if (node && node.textContent.trim() === SELECT_SIZE_LABEL) node.textContent = ' Add to cart ';
      }
    }
  };

  for (const input of inputs) input.addEventListener('change', release, { once: true });
}

function carrySize(value) {
  const inputs = [...document.querySelectorAll('.product-details .variant-option--buttons input')];
  if (!inputs.length) return;

  const match = value
    ? inputs.find((input) => input.value === value && input.dataset.optionAvailable === 'true')
    : null;

  if (match instanceof HTMLElement) {
    if (!match.checked) match.click();
    return;
  }

  for (const input of inputs) input.checked = false;
  awaitSizeChoice(inputs);
}

/**
 * @param {string} url the sibling's product URL
 * @param {'push'|'none'} historyMode 'push' for a click, 'none' when popstate has already
 *   moved the history pointer and the section is only catching up.
 */
async function swapColour(url, historyMode = 'push') {
  const section = productSection();
  if (!section) return false;

  const sectionId = section.id.replace(/^shopify-section-/, '');
  const target = new URL(url, window.location.origin);
  const request = new URL(target);
  request.searchParams.set('section_id', sectionId);

  swapController?.abort();
  swapController = new AbortController();

  const size = currentSizeValue();
  const scroll = window.scrollY;

  const response = await fetch(request, { signal: swapController.signal });
  if (!response.ok) throw new Error(`section ${response.status}`);
  const html = await response.text();

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const fresh = parsed.querySelector('.shopify-section') ?? parsed.body.firstElementChild;
  if (!fresh) throw new Error('no section in the response');

  // The bar and its sheet live on the body and belong to the product being replaced
  for (const selector of ['[data-rubato-sticky-bar]', '[data-rubato-size-sheet]']) {
    for (const node of document.querySelectorAll(selector)) node.remove();
  }

  section.innerHTML = fresh.innerHTML;

  // Each colour is its own entry, so Back steps through the colours. On a popstate the
  // pointer has already moved and touching history again would fight the browser.
  const href = target.pathname + target.search;
  if (historyMode === 'push') {
    history.replaceState({ ...(history.state ?? {}), rubatoColour: href }, '', href);
  }
  renderedProductUrl = href;

  // The head follows the product, without holding up the swap
  fetch(target)
    .then((r) => r.text())
    .then((page) => {
      const doc = new DOMParser().parseFromString(page, 'text/html');
      const title = doc.querySelector('title')?.textContent;
      if (title) document.title = title;
      const canonical = doc.querySelector('link[rel="canonical"]')?.getAttribute('href');
      const own = document.querySelector('link[rel="canonical"]');
      if (canonical && own) own.setAttribute('href', canonical);
    })
    .catch(() => {});

  init();
  carrySize(size);

  // The size guide is a separate module and has no way to know the markup changed under it.
  document.dispatchEvent(new CustomEvent('rubato:product-swapped'));

  window.scrollTo(0, scroll);
  return true;
}

function setUpColourSwap(root = document) {
  const row = root.querySelector('[data-rubato-colour]');
  if (!row || row.dataset.rubatoSwapWired === 'true') return;
  row.dataset.rubatoSwapWired = 'true';

  row.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-rubato-colour-swap]') : null;
    if (!link) return;
    // A new tab, a new window or a download is the browser's business
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    const href = link.getAttribute('href');
    swapColour(href, 'push').catch((error) => {
      if (error.name === 'AbortError') return;
      window.location.href = href;
    });
  });
}

/**
 * Back and Forward across the colours pushed above.
 *
 * Wired once, on the window, rather than per section: the section's markup is replaced on
 * every swap and a listener inside it would be thrown away with the old colour.
 *
 * Only entries that are still a product page are handled here. Stepping back past the
 * first colour lands on a different document and the browser does that itself, without
 * this handler ever running.
 */
let popstateWired = false;

/**
 * Round 61 part 15. With replaceState there are no colour entries to step back through, so
 * this handler has nothing to serve on a colour change. It stays for the one case it still
 * covers: a same-document entry restored by the browser, from bfcache or otherwise, whose URL
 * is a product the section is not currently showing.
 */
function setUpColourHistory() {
  if (popstateWired) return;
  popstateWired = true;

  window.addEventListener('popstate', () => {
    const href = window.location.pathname + window.location.search;
    if (href === renderedProductUrl) return;
    if (!/\/products\//.test(window.location.pathname)) return;
    // No product section on the page means this is not a swap we can serve
    if (!productSection()) return;

    swapColour(href, 'none').catch((error) => {
      if (error.name === 'AbortError') return;
      // The section could not be fetched, so let the browser render the entry properly
      window.location.reload();
    });
  });
}

/** The colour name follows the swatch the pointer is on, and reverts on leave. */
function setUpColourRow(root = document) {
  const row = root.querySelector('[data-rubato-colour]');
  if (!row || row.dataset.rubatoWired === 'true') return;
  row.dataset.rubatoWired = 'true';

  const label = row.querySelector('[data-rubato-colour-label]');
  if (!label) return;
  const fallback = label.dataset.rubatoColourDefault ?? label.textContent.trim();

  for (const swatch of row.querySelectorAll('[data-rubato-colour-name]')) {
    const name = swatch.dataset.rubatoColourName;
    for (const event of ['pointerenter', 'focus']) {
      swatch.addEventListener(event, () => {
        label.textContent = name;
      });
    }
    for (const event of ['pointerleave', 'blur']) {
      swatch.addEventListener(event, () => {
        label.textContent = fallback;
      });
    }
  }
}

/**
 * select_variant for the pixels.
 *
 * Shopify publishes a standard event for almost everything a shopper does, but not for
 * changing size, so nothing downstream ever learns that a 44 became a 46. The theme
 * publishes its own event instead, which a custom pixel in admin subscribes to and forwards
 * to GA4. See docs/rubato-customer-events-pixel.js.
 *
 * The payload has to be complete: a pixel runs in a sandbox and cannot read the page, so
 * everything it needs travels with the event, out of the blob that
 * snippets/rubato-analytics-product.liquid prints. Round 37, analytics.
 */
const VARIANT_EVENT = 'rubato_variant_selected';

/** The product facts printed into the page, parsed once. */
let analyticsProduct;
function readAnalyticsProduct() {
  if (analyticsProduct !== undefined) return analyticsProduct;
  const tag = document.getElementById('rubato-analytics-product');
  try {
    analyticsProduct = tag ? JSON.parse(tag.textContent) : null;
  } catch {
    analyticsProduct = null;
  }
  return analyticsProduct;
}

/** The id the buy form would post, which is the selected variant. */
function selectedVariantId() {
  const field = document.querySelector('.product-details form [name="id"]');
  return field instanceof HTMLInputElement || field instanceof HTMLSelectElement ? field.value : null;
}

/* The first reading is the page the shopper landed on, which is view_item's business, not
   ours. Only a change after that is a selection. */
let lastVariantId = null;

function publishVariantSelected() {
  const variantId = selectedVariantId();
  if (!variantId) return;

  if (lastVariantId === null) {
    lastVariantId = variantId;
    return;
  }
  if (lastVariantId === variantId) return;
  lastVariantId = variantId;

  const facts = readAnalyticsProduct();
  const variant = facts?.variants?.[variantId];
  if (!facts || !variant) return;

  try {
    window.Shopify?.analytics?.publish?.(VARIANT_EVENT, {
      productId: facts.productId,
      variantId: Number(variantId),
      title: facts.title,
      variantTitle: variant.title,
      vendor: facts.vendor,
      type: facts.type,
      sku: variant.sku,
      price: variant.price,
      currency: facts.currency,
    });
  } catch {
    // Analytics must never break the page
  }
}

const NOTIFY_LABEL = 'Notify me';

/**
 * The buy button has three states, and the size that is checked decides which.
 *
 * In stock, it is the filled Add to cart. Nothing chosen yet, it is the same fill at 35
 * percent, which awaitSizeChoice owns and this leaves alone. Sold out, the button itself
 * becomes Notify me: the same 52px in the same place, inverted to a 1px ink border on ground,
 * and it opens the sheet instead of submitting. There is no separate link any more.
 *
 * Horizon decides the sold-out branch on the server at first paint and re-renders the buy
 * column on every variant change, so this runs from init on each pass rather than once.
 * Round 55, replacing round 51's link.
 */
function syncBuyButtons() {
  /* Round 61 part 5. No sheet, no Notify me. The sheet is only rendered when the Klaviyo
     public key is set in theme settings, so its absence is the same question as "can this
     site actually take a back in stock subscription", asked once and in one place. With no
     key the sold-out size keeps Horizon's own disabled Sold out, which is the honest state:
     a Notify me that collects an address nothing will read is worse than no button.

     Round 61 part 23. With Amp the question is answered by the app being there rather than by
     a key, so there is no sheet to look for: Amp brings its own modal and the button only has
     to hand it the variant. Either provider will do; neither means no button. */
  if (backInStockProvider() !== 'amp' && !notifySheet()) return;

  const checked = document.querySelector('.product-details .variant-option input[type="radio"]:checked');
  const soldOut = checked ? checked.dataset.optionAvailable === 'false' : false;

  const buttons = [
    document.querySelector('.product-details .add-to-cart-button'),
    document.querySelector('[data-rubato-sticky-add]'),
  ].filter((button) => button instanceof HTMLButtonElement);

  for (const button of buttons) {
    /* Nothing chosen yet is a state of its own, and it is not ours to overwrite. */
    if (button.classList.contains('rubato-awaiting-size')) continue;

    const node = labelNode(button);
    const label = node ? node.textContent.trim() : '';

    if (soldOut) {
      /* Horizon re-renders this button for the sold-out variant and then morphs it again a
         beat later, putting its own Sold out label and disabled back while our attribute
         survives. So every pass enforces the state rather than only the first one. */
      if (!button.dataset.rubatoBuyType) button.dataset.rubatoBuyType = button.getAttribute('type') ?? 'submit';
      if (node && label && !/sold out/i.test(label) && label !== NOTIFY_LABEL) {
        button.dataset.rubatoBuyLabel = node.textContent;
      }
      button.dataset.rubatoNotifyMode = 'true';
      /* A stable hook for Amp, and for anyone reading the page to see which provider is live.
         The variant is the one that is sold out and selected, which is what Amp needs told. */
      if (backInStockProvider() === 'amp') {
        button.classList.add('rubato-notify');
        button.dataset.backInStock = 'amp';
        /* data-variant-id, not value: the radio's value is the size label, and Amp keys on
           the variant. "XL" reached Amp as a variant id for one round and matched nothing. */
        const variant = checked?.dataset.variantId || '';
        if (variant) button.dataset.variantId = variant;
      }
      /* A type="button" cannot submit the form, whatever else is listening on it. */
      button.setAttribute('type', 'button');
      button.setAttribute('aria-haspopup', 'dialog');
      if (button.disabled) button.disabled = false;
      if (node && label !== NOTIFY_LABEL) node.textContent = ` ${NOTIFY_LABEL} `;
    } else if (button.dataset.rubatoNotifyMode === 'true') {
      button.removeAttribute('data-rubato-notify-mode');
      button.classList.remove('rubato-notify');
      button.removeAttribute('data-back-in-stock');
      button.removeAttribute('data-variant-id');
      button.removeAttribute('aria-haspopup');
      button.setAttribute('type', button.dataset.rubatoBuyType || 'submit');
      /* Only our own label is ours to take back. Horizon owns whatever it rendered. */
      if (node && label === NOTIFY_LABEL) node.textContent = button.dataset.rubatoBuyLabel || ' Add to cart ';
      delete button.dataset.rubatoBuyType;
      delete button.dataset.rubatoBuyLabel;
    }
  }
}

function init() {
  setUpColourRow();
  setUpColourSwap();
  // Guarded internally, so calling it again after a swap costs nothing
  setUpColourHistory();
  setUpZoom();
  setUpSizeGuard();
  setUpReadMore();
  setUpNotify();
  syncBuyButtons();
  setUpBackInStock();
  setUpStickyBar();
  setUpSizeSheet();
  refreshStickyBar();
  applyStickyState();
  positionBuyColumn();
  applyHeaderClearance();
  watchHeaderClearance();
  publishVariantSelected();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// The variant picker re-renders the buy column, so both need rewiring.
let pending = false;
const rewire = () => {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    init();
  });
};

document.addEventListener('shopify:section:load', rewire);
new MutationObserver(rewire).observe(document.body, { childList: true, subtree: true });
